import React, {
  useEffect,
  ReactElement,
  useLayoutEffect,
  useState,
  useRef,
} from 'react';
import { Editor as EditorComponent } from '@tinymce/tinymce-react';
import { Editor } from 'tinymce';
import { useLocation } from 'react-router-dom';

import { showSuccessToast } from 'utils/showToast';

import { useApplicationThemeStore } from '../../../store/useApplicationThemeStore';

function uuidv4() {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16),
  );
}

function wrapInner(parent: any, wrapper: any) {
  if (typeof wrapper === 'string') wrapper = document.createElement(wrapper);
  while (parent.firstChild !== wrapper) wrapper.appendChild(parent.firstChild);
}

const stripHtml = (html: string): string =>
  new DOMParser().parseFromString(html, 'text/html').body.textContent || '';

function TinyMCE({
  value,
  name,
  height,
  setLoading,
  toggleEmbeddedProcedure,
  toggleExistingLayout,
  toggleExistingTable,
  toggleFileImage,
  toggleHyperlink,
  brokenLinks,
  onUserTyped,
  onDetectTableSelection,
  procedureId,
  handleCleanProcedureTemplate,
  setEditorChanges,
  colorScheme,
  disabled = false,
}: {
  value: any;
  name: string;
  height: number;
  setLoading?: (isLoading: boolean) => void;
  toggleEmbeddedProcedure: (
    params:
      | {
        account: string;
        procedureName: string;
      }
      | undefined,
  ) => void;
  toggleExistingLayout: () => void;
  toggleExistingTable: () => void;
  toggleFileImage: () => void;
  toggleHyperlink: () => void;
  setEditorChanges: (value: boolean) => void;
  handleCleanProcedureTemplate: () => any;
  brokenLinks?: Array<any>;
  onUserTyped?: (undo?: boolean) => void;
  onDetectTableSelection?: () => void;
  procedureId?: number;
  colorScheme: string;
  disabled?: boolean;
}): ReactElement | null {
  const [isRefreshing, setRefreshing] = useState(false);
  const [tinyEditor, setTinyEditor] = useState<Editor>();

  const procedureIdRef = useRef<number>();
  const location = useLocation();

  const setTempContent = useApplicationThemeStore(
    React.useCallback(state => state.setTempContent, []),
  );
  useLayoutEffect(() => {
    setLoading && setLoading(true);
    return () => {
      setLoading && setLoading(false);
    };
  }, [setLoading]);

  useEffect(() => {
    const editor = tinyEditor;
    if (typeof editor != 'undefined') {
      if (procedureIdRef.current !== procedureId) {
        procedureIdRef.current = procedureId;
        setRefreshing(true);
        setTimeout(() => {
          setRefreshing(false);
        }, 10);
      }
    }
  }, [procedureId, tinyEditor]);

  useEffect(() => {
    const editor = tinyEditor;
    const url = window.location.href;
    if (editor && editor?.getBody() !== null) {
      const tagCollection = editor?.getBody()?.getElementsByTagName('a');
      //External links in new tab in CC
      for (let i = 0; i < tagCollection.length; i++) {
        if (tagCollection[i].host !== window.location.host) {
          tagCollection[i].target = '_blank'; // removed as part of ticket 41370;
        }
      }
      if (brokenLinks && brokenLinks?.length > 0) {
        for (let j = 0; j < brokenLinks?.length; j++) {
          for (let i = 0; i < tagCollection.length; i++) {
            const href = tagCollection[i].href ?? '';
            const sanitizedHref = href
              .replace(/%(?![0-9A-Fa-f]{2})/g, '%25')
              .replace(/ /g, '%20');
            const decodeHref = decodeURI(sanitizedHref);
            if (
              url.replace(/Procedures\/?/, brokenLinks[j]?.Url) ===
              decodeHref.replace(/Procedures\/?/, '') ||
              stripHtml(brokenLinks[j]?.Url.replace(/\/$/, '')) ==
              decodeHref.replace(/\/$/, '')
            ) {
              tagCollection[i].className = 'fa-cls';
              if (brokenLinks[j].IgnoreIsBroken === true) {
                tagCollection[i].classList.add('dismissed');
              }
              if (
                brokenLinks[j].IgnoreIsBroken === false &&
                tagCollection[i]?.classList.contains('dismissed')
              ) {
                tagCollection[i].classList.remove('dismissed');
              }
            }
          }
        }
        if (brokenLinks?.length !== tagCollection?.length) {
          for (let i = 0; i < tagCollection.length; i++) {
            const href = tagCollection[i].href ?? '';
            const sanitizedHref = href
              .replace(/%(?![0-9A-Fa-f]{2})/g, '%25')
              .replace(/ /g, '%20');
            const decodeHref = decodeURI(sanitizedHref);
            const condition = brokenLinks?.filter(
              (item: any) =>
                url.replace(/Procedures\/?/, '') + item.Url ===
                decodeHref.replace(/Procedures\/?/, '') ||
                stripHtml(item.Url.replace(/\/$/, '')) ===
                decodeHref.replace(/\/$/, ''),
            );
            if (condition?.length === 0) {
              if (
                tagCollection[i].classList.contains('fa-cls') ||
                tagCollection[i].classList.contains('dismissed')
              ) {
                tagCollection[i].classList.remove('fa-cls', 'dismissed');
                tagCollection[i].nextElementSibling?.remove();
              }
            }
          }
        }
      } else {
        for (let i = 0; i < tagCollection.length; i++) {
          if (
            tagCollection[i].classList.contains('fa-cls') ||
            tagCollection[i].classList.contains('dismissed')
          ) {
            tagCollection[i].classList.remove('fa-cls', 'dismissed');
            tagCollection[i].nextElementSibling?.remove();
          }
        }
      }
    }
  }, [brokenLinks, tinyEditor, location]);

  if (isRefreshing) return null;

  const handleBrokenLinkIndication = (editor: any) => {
    const url = window.location.href;
    if (editor && editor?.getBody() !== null) {
      const tagCollection = editor?.getBody()?.getElementsByTagName('a');
      //External links in new tab in CC
      for (let i = 0; i < tagCollection.length; i++) {
        if (tagCollection[i].host !== window.location.host) {
          tagCollection[i].target = '_blank'; // removed as part of ticket 41370;
        }
      }
      if (brokenLinks && brokenLinks?.length > 0) {
        for (let j = 0; j < brokenLinks?.length; j++) {
          for (let i = 0; i < tagCollection.length; i++) {
            const href = tagCollection[i].href ?? '';
            const sanitizedHref = href
              .replace(/%(?![0-9A-Fa-f]{2})/g, '%25')
              .replace(/ /g, '%20');
            const decodeHref = decodeURI(sanitizedHref);
            if (
              url.replace(/Procedures\/?/, '') + brokenLinks[j]?.Url ===
              decodeHref.replace(/Procedures\/?/, '') ||
              stripHtml(brokenLinks[j]?.Url.replace(/\/$/, '')) ==
              decodeHref.replace(/\/$/, '')
            ) {
              tagCollection[i].className = 'fa-cls';
              if (brokenLinks[j].IgnoreIsBroken === true) {
                tagCollection[i].classList.add('dismissed');
              }
              if (
                brokenLinks[j].IgnoreIsBroken === false &&
                tagCollection[i]?.classList.contains('dismissed')
              ) {
                tagCollection[i].classList.remove('dismissed');
              }
            }
          }
        }
      }
    }
  };

  return (
    <EditorComponent
      apiKey="qiu5z2v4blb5bj2osks7butrjd3q4ms2rrnht9tfoa277k6j"
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      // tinymceScriptSrc="/tinymce/tinymce.min.js"
      // licenseKey="T8LK:eyJhbGciOiJFUzI1NiIsIng1YyI6WyJNSUlCbURDQ0FUMmdBd0lCQWdJVWJTS2NpUDJROHBNTFJTc29JS29ucUNXYmw1VXdDZ1lJS29aSXpqMEVBd0l3UHpFYU1CZ0dBMVVFQ2d3UlZHbHVlU0JVWldOb2JtOXNiMmRwWlhNeElUQWZCZ05WQkFNTUdGUnBibmxOUTBVZ09DQnNhV05sYm5ObElHdGxlU0JEUVRBZ0Z3MHlOVEEwTVRBd05ESTJNVEJhR0E4eU1EYzFNREV3TVRBME1qWXhNRm93RWpFUU1BNEdBMVVFQXd3SFZEaE1TMU10VURCWk1CTUdCeXFHU000OUFnRUdDQ3FHU000OUF3RUhBMElBQkdnTjlqUG5Ga2o1Q0VSbEJDUkNMNGliOXBDMUo5U3p4ajBmckU1QkgxdE8yaEZYQmlNd2VlRjFheEh6cjBEcnhycUE0dDNFbTJZaUYyekk4ZVFoa2dHalFqQkFNQjBHQTFVZERnUVdCQlE0dU42blREemN1YmlHMXVET3Q4OUlQMkFWQlRBZkJnTlZIU01FR0RBV2dCVHFoanNsN0lWdEZFYVMvaFNSUXhiT2ZuaWFRVEFLQmdncWhrak9QUVFEQWdOSkFEQkdBaUVBMmtHTmVZRnRranYvemFzUEVKWjBQVFZTOW1KdTFaRWYvWklQTGFFSDJ6MENJUURRTnFBL0FFTVFlQno2T0VHNlBteVhFakJVZDVqRDBxcVlwVS9pM1hhL2VRPT0iXX0.CiQwMTliZWNhZC0wNzI5LTdlNDQtOTBlYi03YjUxNWRkN2E2MTTCAhUKDAj_pZPNBhDAj67cAxIFCICangGwBAjSBR0CAwQFFgcICQoLDg8jERIUFRcYGRofHB0eIBIfHg.ymQHGAE3v4VF4aQh8vX8VMH0H-V8fUzgavtbdSEO8VJS0-NN4o9MY8QNLyGqy_YPMXAYepnEDMzWndJJNwr2Jw"
      onInit={(_, editor) => {
        setLoading && setLoading(false);
        setTinyEditor(editor);
        handleBrokenLinkIndication(editor);
        // readonly: true;
      }}
      disabled={disabled}
      textareaName={name}
      value={value}
      init={{
        license_key:
          'T8LK:eyJhbGciOiJFUzI1NiIsIng1YyI6WyJNSUlCbURDQ0FUMmdBd0lCQWdJVWJTS2NpUDJROHBNTFJTc29JS29ucUNXYmw1VXdDZ1lJS29aSXpqMEVBd0l3UHpFYU1CZ0dBMVVFQ2d3UlZHbHVlU0JVWldOb2JtOXNiMmRwWlhNeElUQWZCZ05WQkFNTUdGUnBibmxOUTBVZ09DQnNhV05sYm5ObElHdGxlU0JEUVRBZ0Z3MHlOVEEwTVRBd05ESTJNVEJhR0E4eU1EYzFNREV3TVRBME1qWXhNRm93RWpFUU1BNEdBMVVFQXd3SFZEaE1TMU10VURCWk1CTUdCeXFHU000OUFnRUdDQ3FHU000OUF3RUhBMElBQkdnTjlqUG5Ga2o1Q0VSbEJDUkNMNGliOXBDMUo5U3p4ajBmckU1QkgxdE8yaEZYQmlNd2VlRjFheEh6cjBEcnhycUE0dDNFbTJZaUYyekk4ZVFoa2dHalFqQkFNQjBHQTFVZERnUVdCQlE0dU42blREemN1YmlHMXVET3Q4OUlQMkFWQlRBZkJnTlZIU01FR0RBV2dCVHFoanNsN0lWdEZFYVMvaFNSUXhiT2ZuaWFRVEFLQmdncWhrak9QUVFEQWdOSkFEQkdBaUVBMmtHTmVZRnRranYvemFzUEVKWjBQVFZTOW1KdTFaRWYvWklQTGFFSDJ6MENJUURRTnFBL0FFTVFlQno2T0VHNlBteVhFakJVZDVqRDBxcVlwVS9pM1hhL2VRPT0iXX0.CiQwMTljZGVjNy1lZTAwLTcwYWUtYTU1Yi02YTlmODM3YmE4OGHCAhUKDAj_6ZLcBhDAj67cAxIFCICangGwBAjSBR0CAwQFFgcICQoLDg8jERIUFRcYGRofHB0eIBIfHg.wjJpJHunVUTQI2lYbFLKDBqPayvuiIy2cHOfOGF8Q48nr0g2x-tLSpTuu64_ju3xx2PifBEp0VfgWqyPxXlszw',
        readonly: disabled,
        setup: editor => {
          editor.on('init', () => {
            const applyReadOnly = () => {
              const body = editor.getBody();
              const iframe = editor.iframeElement;

              if (!body || !iframe) {
                // Body or iframe not ready yet → try again next frame
                requestAnimationFrame(applyReadOnly);
                return;
              }

              if (disabled) {
                body.setAttribute('contenteditable', 'false');
                body.style.userSelect = 'text';
                body.style.caretColor = 'transparent';
                body.style.cursor = 'default';
                iframe.setAttribute('tabindex', '-1');
              } else {
                body.setAttribute('contenteditable', 'true');
                iframe.setAttribute('tabindex', '0');
                iframe.style.pointerEvents = 'auto';
                body.style.caretColor = 'default';
                body.style.cursor = 'text';
              }
            };

            applyReadOnly(); // start retry loop
          });
          let debounceTimeout: any;
          editor.on('keydown', function () {
            const content = editor.getContent();
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
              setTempContent(content);
            }, 10);
          });
          editor.on('mouseover', function () {
            const content = editor.getContent();
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
              setTempContent(content);
            }, 10);
            const tagCollection: HTMLAnchorElement[] = (
              window as any
            )?.tinymce?.activeEditor
              ?.getBody()
              ?.getElementsByTagName('a');
            for (let i = 0; i < tagCollection.length; i++) {
              if (
                (window as any)?.tinymce?.activeEditor
                  ?.getBody()
                  ?.getElementsByTagName('a')
                [i]?.href.includes('hyper_link')
              ) {
                tagCollection[i].title = tagCollection[i]?.classList.contains(
                  'dismissed',
                )
                  ? `Dismissed Broken Link - ${(tagCollection[i].title = (
                    window as any
                  )?.tinymce?.activeEditor
                    ?.getBody()
                    ?.getElementsByTagName('a')
                  [i]?.href?.split('api')[1])}`
                  : (tagCollection[i].title = (
                    window as any
                  )?.tinymce?.activeEditor
                    ?.getBody()
                    ?.getElementsByTagName('a')
                  [i]?.href?.split('api')[1]);
              } else {
                const href = tagCollection[i].href ?? '';
                const sanitizedHref = href
                  .replace(/%(?![0-9A-Fa-f]{2})/g, '%25')
                  .replace(/ /g, '%20');
                tagCollection[i].title = tagCollection[i]?.classList.contains(
                  'dismissed',
                )
                  ? `Dismissed Broken Link - ${decodeURI(sanitizedHref)}`
                  : decodeURI(sanitizedHref);
              }
            }
          });
          editor.ui.registry.addButton('hyperlink', {
            icon: undefined,
            tooltip: 'Hyperlink',
            text: 'Hyperlink',
            onAction: function () {
              toggleHyperlink();
            },
          });
          editor.ui.registry.addMenuItem('hyperlink', {
            text: 'Set As Link',
            onAction: () => {
              const id = uuidv4();
              const content = (window as any)?.tinymce?.activeEditor?.selection
                ?.getContent({ format: 'text' })
                ?.trim();
              if (content != '') {
                const node = (
                  window as any
                )?.tinymce?.activeEditor?.selection?.getNode();
                if (node.getAttribute('id')) {
                  wrapInner(
                    node,
                    "<span id='" +
                    `hyper_link-${id}` +
                    "',data-linkText='" +
                    content +
                    "' ></span>",
                  );
                } else {
                  node.setAttribute('id', `hyper_link-${id}`);
                  node.setAttribute('data-linkText', content);
                }
                showSuccessToast({ message: 'Set selected text as link' });
              }
            },
          });
          editor.ui.registry.addMenuItem('Copy-Link', {
            text: 'Copy-Link',
            onAction: () => {
              let selectedText;
              if (
                (window as any)?.tinymce?.activeEditor?.selection
                  ?.getNode()
                  ?.href?.includes('hyper_link')
              ) {
                selectedText = (window as any)?.tinymce?.activeEditor?.selection
                  ?.getNode()
                  ?.href.split('api')[1];
              } else {
                selectedText = (
                  window as any
                )?.tinymce?.activeEditor?.selection?.getNode()?.href;
              }
              const element = document.createElement('textarea');
              element.value = selectedText;
              document.body.appendChild(element);
              element.select();
              document.execCommand('copy');
              document.body.removeChild(element);
            },
          });
          editor.ui.registry.addButton('FileImage', {
            icon: undefined,
            tooltip: 'File/Image',
            text: 'File/Image',
            onAction: function () {
              toggleFileImage();
            },
          });
          editor.ui.registry.addButton('EmbeddedProcedure', {
            icon: undefined,
            tooltip: 'Embedded-Procedure',
            text: 'Embedded-Procedure',
            onAction: function () {
              toggleEmbeddedProcedure(undefined);
            },
          });
          editor.ui.registry.addMenuButton('Template', {
            icon: undefined,
            tooltip: 'Template',
            text: 'Template',
            fetch: function (callback: any) {
              const menuItems = [
                {
                  type: 'menuitem',
                  text: 'Layout',
                  onAction: function () {
                    toggleExistingLayout();
                  },
                },
                {
                  type: 'menuitem',
                  text: 'Table',
                  onAction: function () {
                    toggleExistingTable();
                  },
                },
              ];
              callback(menuItems);
            },
          });
          editor.ui.registry.addButton('CleanTemplate', {
            icon: undefined,
            tooltip: 'Clean Template',
            text: 'Clean Template',
            onAction: function () {
              handleCleanProcedureTemplate();
            },
          });
        },
        skin: colorScheme == 'dark' ? 'oxide-dark' : 'oxide',
        // skin_url:
        //   colorScheme == 'dark'
        //     ? '/tinymce/skins/ui/oxide-dark'
        //     : '/tinymce/skins/ui/oxide',
        content_css:
          'https://stackpath.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css',
        height: height || 500,
        autolink_pattern:
          /^(https?:\/\/|ssh:\/\/|ftp:\/\/|file:\/|www\.)(.+)$/i,
        font_family_formats:
          'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Cambria Serif=Cambria, Georgia, serif; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Roboto="Roboto",sans-serif; Source Sans Pro="Source Sans Pro", sans-serif; Source Serif Pro="Source Serif Pro", serif; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats',
        menubar: 'file edit view insert format tools table',
        table_toolbar:
          'tableprops tabledelete | tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells',
        plugins: [
          'license_key_manager',
          'a11ychecker ', // preminum plugins
          'advcode',
          // 'advtable',
          'casechange',
          'checklist',
          'comments',
          'export',
          'formatpainter',
          'powerpaste',
          'permanentpen',
          'tableofcontents',
          'tinymcespellchecker',
          'anchor', // open source plugins
          'autolink',
          'advlist',
          'fullscreen',
          'image',
          'link',
          'lists',
          'liststyles',
          'preview',
          'table',
          'visualblocks',
          'searchreplace',
          'insertdatetime',
          'tinycomments',
        ],
        forced_root_block_attrs: {
          style: 'margin: 0',
        },
        // table_column_resizing: false,
        tinycomments_mode: 'embedded',
        tinycomments_author: 'Author',
        a11y_advanced_options: true,
        pad_empty_with_br: false,
        toolbar1:
          'undo redo | fontfamily | fontsize | blocks | bold italic underline  | alignleft aligncenter alignright alignjustify |bullist numlist outdent indent | forecolor backcolor | print | fullpage | unlink | hyperlink | FileImage | Template|  EmbeddedProcedure |  a11ycheck | formatpainter | permanentpen | checklist | casechange | addcomment showcomments | CleanTemplate | export',
        convert_urls: false,
        content_css_cors: true,
        // extended_valid_elements: 'i[*]',
        extended_valid_elements: 'i[*],style[type|media]',
        verify_html: false,
        // table_column_resizing: 'preservetable',
        // table_use_colgroups: true,
        // table_default_styles: {
        //   width: '100%',
        // },
        table_resize_bars: true,
        table_column_resizing: 'resizetable',
        table_default_styles: {
          width: '100%',
        },

        // pad_empty_with_br: false,
        content_style: `
        @import url('https://fonts.googleapis.com/css2?family=Roboto&family=Source+Sans+Pro&family=Source+Serif+Pro&display=swap');
         .mce-content-body {
              font-family:Arial;
              font-size:10pt;
              margin: 1rem;
            }
            .fa-cls {
              display: inline-block;
              color:indianred;
            }
            .fa-cls::after {
              font-family: FontAwesome;
              content: '\\f0c1';
              padding-inline-start: 0.05rem;
              text-decoration: none;
            }
            sup i.fa-link {
              display: none;
            }
            a.fa-cls + sup i.fa-link {
              display: inline;
            }
            .dismissed, .dismissed + sup, .dismissed + i {
              color: #888 !important ;
            }
            #navcontainer {
              width: 600px;
          }
                  img {
                      max-inline-size: 100% !important; 
                      max-block-size: auto !important
                  }
        a { cursor: default !important; }
      
ttable {
    width: 100% !important;
    min-width: 100% !important;
    border-collapse: collapse;
  }

  td, th {
    vertical-align: middle;
    height: 24px;
  }
            `,
        // table_use_colgroups: true,
        spellchecker_active: true,
        //valid_children: '* [*]',
        valid_children:
          '+body[style],+head[style|title|base|link],+html[head|body|style]',
        contextmenu: false,
        link_title: false,
      }}
      key={colorScheme}
      onEditorChange={(content, editor) => {
        setEditorChanges(true);
        setTinyEditor(editor);
        handleBrokenLinkIndication(editor);
      }}
      onKeyDown={() => {
        if (onUserTyped) onUserTyped();
      }}
      onMouseDown={() => {
        if (onUserTyped) onUserTyped();
      }}
      onNodeChange={onDetectTableSelection}
      onUndo={() => {
        if (onUserTyped) onUserTyped(true);
      }}
      onRedo={() => {
        if (onUserTyped) onUserTyped(true);
      }}
    />
  );
}

export { TinyMCE };
